<template>
  <div class="flex flex-col w-full">

    <div class="flex items-center text-xs text-gray-500 mb-3">
      <b-icon pack="fas" icon="circle-info" size="is-small" class="mr-2" />
      <span>{{ $t('pictogram_boqs_settings_description') }}</span>
    </div>

    <b-table
      :data="visibleLayers"
      :loading="isLoading"
      :bordered="true"
      icon-pack="fas"
      :row-key="'name'">

      <b-table-column field="name" :label="$t('design_layer_name')" sortable v-slot="props">
        <div class="flex items-center">
          <layer-color-icon :color="props.row.color" class="mr-2" />
          <span>{{ props.row.name }}</span>
        </div>
      </b-table-column>

      <!-- Mecánico: pares línea/orden para estructuras y módulos. -->
      <b-table-column v-if="isMechanical" field="structure_order_line_id" :label="$t('structure_order_line_id')" v-slot="props">
        {{ _GetLineId(props.row.boqLinks, 'structureLine') }}
      </b-table-column>

      <b-table-column v-if="isMechanical" field="structure_order_id" :label="$t('structure_order_id')" v-slot="props">
        {{ _GetOrderId(props.row.boqLinks, 'structureOrder') }}
      </b-table-column>

      <b-table-column v-if="isMechanical" field="module_order_line_id" :label="$t('module_order_line_id')" v-slot="props">
        {{ _GetLineId(props.row.boqLinks, 'moduleLine') }}
      </b-table-column>

      <b-table-column v-if="isMechanical" field="module_order_id" :label="$t('module_order_id')" v-slot="props">
        {{ _GetOrderId(props.row.boqLinks, 'moduleOrder') }}
      </b-table-column>

      <!-- Eléctrico / zanjas: línea + zona + orden de pedido por capa. -->
      <b-table-column v-if="!isMechanical" field="order_line_id" :label="$t('order_line_id')" v-slot="props">
        {{ _GetLineId(props.row.boqLinks, 'line') }}
      </b-table-column>

      <b-table-column v-if="!isMechanical" field="zone" :label="$t('zone')" v-slot="props">
        {{ _GetZone(props.row.boqLinks) }}
      </b-table-column>

      <b-table-column v-if="!isMechanical" field="order_id" :label="$t('order_id')" v-slot="props">
        {{ _GetOrderId(props.row.boqLinks, 'order') }}
      </b-table-column>

      <b-table-column field="actions" :label="$t('actions')" width="80" v-slot="props">
        <b-tooltip :label="$t('edit')" position="is-left">
          <div @click="_HandleEdit(props.row)"
               class="flex cursor-pointer text-sky-300">
            <b-icon pack="fas" icon="pen" />
          </div>
        </b-tooltip>
      </b-table-column>

      <template #empty>
        <div class="has-text-centered">{{ $t('no_results') }}</div>
      </template>
    </b-table>

    <!-- Modal genérico reutilizable — carga PictogramBoqLayerLinksModal on-demand. -->
    <modal-wrapper ref="modal" />
  </div>
</template>

<script>
import ModalWrapper from '@pictograms/shared/components/modals/ModalWrapper.vue';
import LayerColorIcon from '@pictograms/shared/components/icons/LayerColorIcon.vue';
import PictogramBoqLayerLinksModal from './PictogramBoqLayerLinksModal.vue';
import { getPictogramDesign } from '../../services/pictograms.service.js';
import { PICTOGRAM_TYPES } from '../../constants/pictogram-types.js';

// Capas de mecánico que se ocultan en la tabla de BOQs (por nombre exacto o prefijo TOKEN_).
const HIDDEN_MECHANICAL_LAYER_TOKENS = ['MODULES', 'PILINGS', 'AXES'];

export default {
  name: 'PictogramBoqsSettings',

  components: {
    ModalWrapper,
    LayerColorIcon,
  },

  props: {
    projectId: { type: Number, required: true },
    entity:    { type: Object, required: true },  // pictograma
    entityType:{ type: String, required: true },
  },

  data() {
    return {
      isLoading: false,
      layers: [],
    };
  },

  computed: {
    // El pictograma es mecánico cuando su type es PICTOGRAM_TYPES.MECHANICAL.
    isMechanical() {
      return !!this.entity && this.entity.type === PICTOGRAM_TYPES.MECHANICAL;
    },

    // En mecánico ocultamos las capas cuyo nombre sea o empiece por
    // MODULES_ / PILINGS_ / AXES_.
    visibleLayers() {
      if (!this.isMechanical) {
        return this.layers;
      }
      return this.layers.filter((layer) => {
        const name = (layer && layer.name ? String(layer.name) : '').toUpperCase();
        return !HIDDEN_MECHANICAL_LAYER_TOKENS.some((token) => name === token || name.startsWith(`${token}_`));
      });
    },
  },

  mounted() {
    this._LoadDesignLayers();
  },

  watch: {
    // Al cambiar de pictograma (p.ej. de mecánico a eléctrico) el componente dinámico
    // se reutiliza (mismo `PictogramBoqsSettings`, sin :key en el padre), así que
    // mounted() no se vuelve a ejecutar. Recargamos las capas al cambiar de entidad.
    'entity.id'(newId, oldId) {
      if (newId && newId !== oldId) {
        this._LoadDesignLayers();
      }
    },
  },

  methods: {
    async _LoadDesignLayers() {
      this.isLoading = true;
      try {
        const data = await getPictogramDesign(this.entity.id);
        if (data && data.success && data.pictogramDesign) {
          this.layers = (data.pictogramDesign.metadata && data.pictogramDesign.metadata.layers) || [];
        } else {
          this.layers = [];
        }
      } catch (e) {
        this.$notify.error(this.$t('error_loading_documents'));
      } finally {
        this.isLoading = false;
      }
    },

    _HandleEdit(row) {
      this.$refs.modal.show({
        component: PictogramBoqLayerLinksModal,
        title:     this.$t('edit_layer_boq_links'),
        width:     720,
        props: {
          projectId:     this.projectId,
          pictogramId:   this.entity.id,
          pictogramType: this.entity.type,
          layer:         row,
        },
        listeners: {
          saved: () => this._LoadDesignLayers(),
        },
      });
    },

    // Lee de forma segura el "line_pedido" del snapshot embebido en Mongo.
    // Muestra `line_pedido` cuando existe, si no cae al `code` de la BOQ.
    _GetLineId(boqLinks, key) {
      const snap = boqLinks && boqLinks[key];
      if (!snap) return '—';
      return snap.line_pedido || snap.code || '—';
    },

    // Lee el `code` de la work-order del snapshot embebido.
    _GetOrderId(boqLinks, key) {
      const snap = boqLinks && boqLinks[key];
      if (!snap) return '—';
      return snap.code || '—';
    },

    // Zona (eléctrico): guardada dentro del snapshot de la línea (zone_name/code).
    _GetZone(boqLinks) {
      const line = boqLinks && boqLinks.line;
      if (!line) return '—';
      return line.zone_name || line.zone_code || '—';
    },
  },
};
</script>
